import * as d3scale from 'd3-scale';
import { getDefaultLineForThreshold, getThresholdFeature, isDefaultLineStaticRefLine } from '.';

import { getDateRange } from '../../dateUtils';
import { DataRow, DataTable, Value } from '../../models';
import { accessorForReference } from '../../models/Accessor';
import { ColumnReference, EffectiveDefinition } from '../../models/EffectiveDefinition';
import { getMeasureFeature, getTimestampFeature } from '../metricValue';

export type VisualTableExtender = (totalWidth: number, rightMargin: number, table: DataTable) => DataTable;

/**
 * Creates a function which will extend static threshold lines from the definition
 * through the right margin in card views.
 *
 * In any points added, the column pointed to by the `measure` feature will be
 * explicitly set to `null` to indicate that it should be rendered with no
 * value, rather than dropped.
 *
 * @param def The `EffectiveDefinition` to use
 * @returns A function to be called with `totalWidth`, `rightMargin`, and the table to extend
 */
export const makeVisualTableExtender = (def: EffectiveDefinition): VisualTableExtender => {
  const columnsToExtend = columnsToExtendThroughRightMargin(def);
  const timestampColumn = getTimestampFeature(def.features);
  const columnsToNull = [getMeasureFeature(def.features)];
  return (totalWidth: number, rightMargin: number, table: DataTable) => {
    const spec: ExtendThroughRightMargin = {
      totalWidth,
      rightMargin,
      timestampColumn,
      columnsToExtend,
      columnsToNull,
    };

    return extendThroughRightMargin(spec, table);
  };
};

/**
 * A specification for how to exectue `extendThroughRightMargin`
 *
 * @param totalWidth The total width of the visualization area
 * @param rightMargin The amount of right margin space to extend through
 * @param timestampColumn The timestamp column to add a point to
 * @param columnsToExtend The columns that should have their most recent value copied to the new extended point
 * @param columnsToNull The columns that should be explicitly set to `null` in the added point
 */
export interface ExtendThroughRightMargin {
  readonly totalWidth: number;
  readonly rightMargin: number;
  readonly timestampColumn: ColumnReference;
  readonly columnsToExtend: ReadonlyArray<ColumnReference | undefined>;
  readonly columnsToNull?: ReadonlyArray<ColumnReference>;
}

/**
 * Modifies a table by adding a new point to the end, such that the appearance
 * of a right margin is maintained for the primary line, while extending the
 * latest values of the given `columnsToCopy` through the margin.
 *
 * @param spec The specification to use
 * @param table The table to extend
 * @returns The modified table
 */
export const extendThroughRightMargin = (spec: ExtendThroughRightMargin, table: DataTable): DataTable => {
  const timeDomain = getDateRange(table, accessorForReference(spec.timestampColumn));

  if (!timeDomain) {
    return table;
  }

  if (spec.rightMargin === 0) {
    return table;
  }

  const timeScale = d3scale
    .scaleLinear()
    .domain([timeDomain?.first, timeDomain?.last] as [Date, Date])
    .range([0, spec.totalWidth - spec.rightMargin]);

  const timestamp = timeScale.invert(spec.totalWidth);

  const latestPoint = table[table.length - 1];
  const pointToAdd = generatePointToAdd(latestPoint, spec, timestamp);
  return [...table, pointToAdd];
};

export const shouldExtendThroughRightMargin = (definition: EffectiveDefinition): boolean => {
  return isDefaultLineStaticRefLine(definition);
};

export const columnsToExtendThroughRightMargin = (
  definition: EffectiveDefinition
): ReadonlyArray<ColumnReference | undefined> => {
  const feature = getThresholdFeature(definition.features);
  const comparisonMeasureColumnRef = getDefaultLineForThreshold(feature)?.measure;

  return shouldExtendThroughRightMargin(definition) ? [comparisonMeasureColumnRef] : [];
};

const generatePointToAdd = (latestPoint: DataRow, spec: ExtendThroughRightMargin, timestamp: number): DataRow => {
  interface RefAndValue {
    ref: ColumnReference | undefined;
    value: Value;
  }

  const refToRefAndValue = (ref: ColumnReference | undefined): RefAndValue => ({
    ref,
    value: ref ? accessorForReference(ref)(latestPoint) : undefined,
  });

  const writeValueToRow = (row: DataRow, { ref, value }: RefAndValue): DataRow => {
    if (ref && value !== undefined) {
      row[ref.column] = value;
    }
    return row;
  };

  const refsWithNullValues = spec.columnsToNull?.map((ref) => ({ ref, value: null })) ?? [];

  const copiedValues = spec.columnsToExtend
    .map(refToRefAndValue)
    .concat(refsWithNullValues)
    .reduce(writeValueToRow, {});
  const pointToAdd = { ...copiedValues, [spec.timestampColumn.column]: new Date(timestamp) };
  return pointToAdd;
};
